import React, { useCallback, useEffect, useState } from "react";
import { Home } from "lucide-react";
import { toast } from "sonner";

import {
  listKeyReceipts,
  getHandoverEmailPreview,
  sendHandoverEmail,
} from "../../../api/adminData";
import {
  summarizeMoveInInspection,
  summarizeMoveOutInspection,
} from "./tenantDetailHelpers";
import KeyReceiptModal from "./KeyReceiptModal";
import { formatDateTime } from "../../../utils/datetime";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";

/**
 * "Übergabe" grouping for one tenancy (K1b-1): two compact cards —
 * Übergabeprotokoll (reuses the existing inspection open-or-create flow,
 * unchanged) and Schlüsselquittung (opens the full KeyReceiptSection inside a
 * non-animated modal). This entry point replaces the old inline KeyReceiptSection
 * + the accordion "handover" item, so the two halves of the handover live side
 * by side. No data-model coupling — both still hang off the tenancy independently.
 */

const btnCls =
  "rounded-[6px] border border-black/10 bg-transparent px-[10px] py-[3px] text-[10px] text-[#64748b] transition-colors hover:bg-slate-100 dark:border-white/[0.1] dark:text-[#8090b0] dark:hover:bg-white/[0.04]";

const cardCls =
  "flex flex-col gap-2 rounded-[8px] border border-black/10 p-3 dark:border-white/[0.08]";

// Status chip for the key receipt — derived from the SAME row KeyReceiptSection
// uses (newest row = items[0], list is ordered created_at DESC). Fields come from
// listKeyReceipts rows: status / pdf_object_key / signed_at.
function keyReceiptStatusChip(row) {
  if (row === undefined) return { label: "…", cls: "bg-slate-100 text-[#64748b] dark:bg-white/[0.06] dark:text-[#93a4bf]" };
  if (row === null) return { label: "Status nicht geladen", cls: "bg-slate-100 text-[#64748b] dark:bg-white/[0.06] dark:text-[#93a4bf]" };
  if (!row.exists) return { label: "Keine Quittung", cls: "bg-slate-100 text-[#64748b] dark:bg-white/[0.06] dark:text-[#93a4bf]" };
  if (row.signed_at) return { label: "Unterzeichnet", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/[0.12] dark:text-emerald-300" };
  if (row.pdf_object_key) return { label: "Generiert", cls: "bg-blue-100 text-blue-700 dark:bg-blue-500/[0.12] dark:text-blue-300" };
  return { label: "Entwurf", cls: "bg-amber-100 text-amber-700 dark:bg-amber-500/[0.12] dark:text-amber-300" };
}

export default function HandoverSection({
  tenancy,
  inspectionsList,
  inspectionDetailById,
  openOrCreateInspection,
  // M1c-E: raised after a key receipt is successfully signed, so the card's
  // document list can catch up with the projection the backend just wrote.
  // Optional — this section is useful without it.
  onKeyReceiptSigned,
}) {
  // krRow: undefined = loading, null = fetch error, {exists:false} = none,
  // {exists:true, signed_at, pdf_object_key} = newest receipt.
  const [krRow, setKrRow] = useState(undefined);
  const [krOpen, setKrOpen] = useState(false);

  // Handover email: preview = undefined (loading) | null (fetch error) | object.
  const [preview, setPreview] = useState(undefined);
  const [previewErr, setPreviewErr] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);

  const loadKrStatus = useCallback(async () => {
    if (!tenancy?.id) return;
    try {
      const data = await listKeyReceipts(tenancy.id);
      const rows = Array.isArray(data?.items) ? data.items : [];
      const row = rows[0]; // same row KeyReceiptSection uses (newest)
      setKrRow(
        row
          ? { exists: true, signed_at: row.signed_at, pdf_object_key: row.pdf_object_key }
          : { exists: false }
      );
    } catch {
      // A failed status fetch must never crash the tenancy card.
      setKrRow(null);
    }
  }, [tenancy?.id]);

  useEffect(() => {
    loadKrStatus();
  }, [loadKrStatus]);

  const handleKrClose = () => {
    setKrOpen(false);
    loadKrStatus(); // reflect any change made inside the modal
    loadPreview(); // signing inside the modal can change sendable documents
  };

  // Load the handover-email preview. The backend is the source of truth for
  // recipient + sendable documents + can_send. Mount-load is consistent with
  // this section already fetching on mount (listKeyReceipts) and makes the
  // button label/disabled meaningful immediately. A failure is isolated to this
  // card (the protocol + key-receipt cards keep working).
  const loadPreview = useCallback(async () => {
    if (!tenancy?.id) return;
    setPreview(undefined);
    setPreviewErr("");
    try {
      const data = await getHandoverEmailPreview(tenancy.id);
      setPreview(data);
    } catch (err) {
      setPreview(null);
      setPreviewErr(err?.message || "Vorschau konnte nicht geladen werden.");
    }
  }, [tenancy?.id]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  const handleSendEmail = async () => {
    if (sending || !tenancy?.id) return;
    setSending(true);
    try {
      await sendHandoverEmail(tenancy.id);
      toast.success("Übergabeunterlagen wurden per E-Mail gesendet.");
      setConfirmOpen(false);
      await loadPreview();
    } catch (err) {
      // Keep the dialog open so the admin can retry / see the error.
      toast.error(err?.message || "E-Mail konnte nicht versendet werden.");
    } finally {
      setSending(false);
    }
  };

  const labelCls =
    "mb-2 text-[10px] font-bold uppercase tracking-[0.8px] text-[#64748b] dark:text-[#6b7a9a]";

  // ── Übergabeprotokoll card (inspection) — same logic as the old accordion ──
  const miInv = inspectionsList?.find(
    (x) => String(x.tenancy_id) === String(tenancy.id) && String(x.type) === "move_in"
  );
  const moInv = inspectionsList?.find(
    (x) => String(x.tenancy_id) === String(tenancy.id) && String(x.type) === "move_out"
  );
  const miDetail = miInv ? inspectionDetailById?.[miInv.id] || miInv : null;
  const moDetail = moInv ? inspectionDetailById?.[moInv.id] || moInv : null;

  const chip = keyReceiptStatusChip(krRow);
  const krButtonLabel =
    krRow && krRow.exists ? "Schlüsselquittung öffnen" : "Schlüsselquittung erstellen";

  // Handover-email card derivations — sendability comes from the preview only.
  const previewLoading = preview === undefined;
  const previewFailed = preview === null;
  const emailDocs =
    preview && Array.isArray(preview.documents)
      ? preview.documents.filter((d) => d.available)
      : [];
  const emailCount = emailDocs.length;
  const canSendEmail = Boolean(preview && preview.can_send);

  let emailStatusText;
  if (previewLoading) emailStatusText = "Wird geladen …";
  else if (previewFailed) emailStatusText = previewErr || "Konnte nicht geladen werden.";
  else if (!preview.recipient_email) emailStatusText = "Keine Empfängeradresse hinterlegt";
  else if (emailCount === 0) emailStatusText = "Keine signierten Unterlagen vorhanden";
  else emailStatusText = `${emailCount} Dokument${emailCount === 1 ? "" : "e"} bereit`;

  let emailBtnLabel;
  if (previewLoading) emailBtnLabel = "Dokumente laden …";
  else if (previewFailed) emailBtnLabel = "Erneut laden";
  else if (!preview.recipient_email) emailBtnLabel = "Keine Empfängeradresse";
  else if (emailCount === 0) emailBtnLabel = "Keine sendbaren Dokumente";
  else emailBtnLabel = `${emailCount} Dokument${emailCount === 1 ? "" : "e"} senden`;

  return (
    <div className="mt-3">
      <p className={labelCls}>
        <span className="inline-flex items-center gap-1.5">
          <Home className="h-3.5 w-3.5" /> Übergabe
        </span>
      </p>
      <p className="mb-2 text-[11px] text-[#64748b] dark:text-[#8090b0]">
        Übergabeprotokoll und Schlüsselquittung für dieses Mietverhältnis.
      </p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {/* Karte 1 — Übergabeprotokoll */}
        <div className={cardCls}>
          <div className="text-[12px] font-semibold text-[#0f172a] dark:text-[#eef2ff]">
            Übergabeprotokoll
          </div>
          <p className="text-[11px] text-[#64748b] dark:text-[#8090b0]">
            Zustand von Zimmer/Wohnung bei Einzug oder Auszug dokumentieren.
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] leading-snug text-[#64748b] dark:text-[#93a4bf]">
            <span>{summarizeMoveInInspection(miDetail)}</span>
            <span>{summarizeMoveOutInspection(moDetail)}</span>
          </div>
          <div className="mt-1 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => openOrCreateInspection(tenancy, "move_in")}
              className={btnCls}
              style={{ cursor: "pointer" }}
            >
              {miInv ? "Übergabe ansehen" : "Übergabe starten"}
            </button>
            <button
              type="button"
              onClick={() => openOrCreateInspection(tenancy, "move_out")}
              className={btnCls}
              style={{ cursor: "pointer" }}
            >
              {moInv ? "Abnahme ansehen" : "Abnahme starten"}
            </button>
          </div>
        </div>

        {/* Karte 2 — Schlüsselquittung */}
        <div className={cardCls}>
          <div className="flex items-center justify-between gap-2">
            <div className="text-[12px] font-semibold text-[#0f172a] dark:text-[#eef2ff]">
              Schlüsselquittung
            </div>
            <span className={`inline-flex rounded-full px-[8px] py-[1px] text-[9px] font-semibold ${chip.cls}`}>
              {chip.label}
            </span>
          </div>
          <p className="text-[11px] text-[#64748b] dark:text-[#8090b0]">
            Erhaltene Schlüssel dokumentieren, PDF erstellen und unterschreiben.
          </p>
          <div className="mt-1 flex flex-wrap gap-2">
            <button type="button" onClick={() => setKrOpen(true)} className={btnCls} style={{ cursor: "pointer" }}>
              {krButtonLabel}
            </button>
          </div>
        </div>

        {/* Karte 3 — Dokumente per E-Mail */}
        <div className={cardCls}>
          <div className="text-[12px] font-semibold text-[#0f172a] dark:text-[#eef2ff]">
            Dokumente per E-Mail
          </div>
          <p className="text-[11px] text-[#64748b] dark:text-[#8090b0]">
            Senden Sie die signierten Übergabeunterlagen direkt an den Mieter.
          </p>
          {preview && preview.recipient_email ? (
            <p className="text-[11px] text-[#64748b] dark:text-[#93a4bf]">
              Empfänger: {preview.recipient_email}
            </p>
          ) : null}
          <p className="text-[11px] text-[#64748b] dark:text-[#8090b0]">{emailStatusText}</p>
          <div className="mt-1 flex flex-wrap gap-2">
            {previewFailed ? (
              <button type="button" onClick={loadPreview} className={btnCls} style={{ cursor: "pointer" }}>
                Erneut laden
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmOpen(true)}
                disabled={previewLoading || sending || !canSendEmail}
                className={`${btnCls} disabled:cursor-default disabled:opacity-50`}
              >
                {emailBtnLabel}
              </button>
            )}
          </div>
          {preview && preview.last_sent ? (
            <div className="mt-1 border-t border-black/5 pt-2 text-[10px] text-[#64748b] dark:border-white/[0.06] dark:text-[#6b7a9a]">
              <div>Zuletzt gesendet: {formatDateTime(preview.last_sent.sent_at)}</div>
              {preview.last_sent.recipient_email ? (
                <div>An: {preview.last_sent.recipient_email}</div>
              ) : null}
              {Array.isArray(preview.last_sent.documents) && preview.last_sent.documents.length > 0 ? (
                <div>Dokumente: {preview.last_sent.documents.map((d) => d.label).join(", ")}</div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      <KeyReceiptModal
        open={krOpen}
        tenancyId={tenancy.id}
        onClose={handleKrClose}
        onSigned={onKeyReceiptSigned}
      />

      <Dialog
        open={confirmOpen}
        onOpenChange={(o) => {
          if (!sending) setConfirmOpen(o);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Übergabeunterlagen per E-Mail senden</DialogTitle>
            <DialogDescription>Es werden nur signierte Dokumente versendet.</DialogDescription>
          </DialogHeader>
          <div className="text-[13px] text-[#0f172a] dark:text-[#eef2ff]">
            <div className="mb-3">
              <div className="text-[11px] font-semibold uppercase tracking-[0.4px] text-[#64748b] dark:text-[#93a4bf]">
                Empfänger
              </div>
              <div>{preview?.recipient_name || "—"}</div>
              <div className="text-[12px] text-[#64748b] dark:text-[#93a4bf]">
                {preview?.recipient_email || "—"}
              </div>
            </div>
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.4px] text-[#64748b] dark:text-[#93a4bf]">
                Anhänge
              </div>
              {emailDocs.length > 0 ? (
                <ul className="mt-1 list-disc pl-5">
                  {emailDocs.map((d) => (
                    <li key={d.type}>{d.label}</li>
                  ))}
                </ul>
              ) : (
                <div className="text-[12px] text-[#64748b] dark:text-[#93a4bf]">
                  Keine sendbaren Dokumente.
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setConfirmOpen(false)}
              disabled={sending}
              className={`${btnCls} disabled:cursor-default disabled:opacity-50`}
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={handleSendEmail}
              disabled={sending || emailCount === 0}
              className="rounded-[6px] border border-emerald-600/40 bg-emerald-600 px-[10px] py-[3px] text-[10px] font-semibold text-white transition-colors hover:bg-emerald-700 disabled:cursor-default disabled:opacity-50"
            >
              {sending ? "Wird gesendet …" : "Jetzt senden"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
